import { createHmac, timingSafeEqual } from 'node:crypto';
import type { PrismaClient } from '@prisma/client';
import express, { Router, type Request, type Response } from 'express';
import rateLimit from 'express-rate-limit';
import { z } from 'zod';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler } from '../../core/http.js';
import { loadAuthContext } from '../auth/auth.middleware.js';
import { queueEmail } from '../messaging/messaging.service.js';
import {
  createLead,
  findLeadDuplicates,
  logActivity,
  scheduleLeadFollowUp,
  LEAD_SOURCES,
  SERVICE_INTERESTS,
} from './crm.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

interface CaptureConfig {
  enabled?: boolean;
  key?: string;
  pipelineId?: number | null;
  ownerId?: number | null;
  allowedOrigins?: string[];
}

const MIN_AGE_MS = 3_000; // a human takes longer than this to fill a form
const MAX_AGE_MS = 2 * 3_600_000;

const bodySchema = z.object({
  name: z.string().trim().min(2).max(200),
  email: z.string().trim().toLowerCase().email().max(320).optional().or(z.literal('')),
  phone: z.string().trim().max(30).optional().or(z.literal('')),
  message: z.string().trim().max(2000).optional(),
  service: z.string().trim().max(20).optional(),
  page: z.string().trim().max(300).optional(),
  token: z.string().max(200),
  /** Honeypot: hidden from people, filled in by bots. */
  website_url: z.string().max(500).optional(),
  'cf-turnstile-response': z.string().max(4000).optional(),
});

/**
 * Public website lead capture (PLAN.md 7.5). Mounted BEFORE the app-wide CORS because it answers browsers
 * on other sites: only the origins the owner listed are allowed. Protections: per-IP rate limit, honeypot
 * field, a signed time token (rejects instant and replayed-forever submissions), optional Cloudflare
 * Turnstile, strict validation, and no internal ids in any response.
 */
export function createPublicLeadsRouter({ db, config }: Deps) {
  const router = Router();
  const secret = config.JWT_ACCESS_SECRET;
  const sign = (ts: string, key: string) =>
    createHmac('sha256', secret).update(`lead-form.${key}.${ts}`).digest('hex').slice(0, 32);
  const makeToken = (key: string) => {
    const ts = String(Date.now());
    return `${ts}.${sign(ts, key)}`;
  };
  function checkToken(token: string, key: string) {
    const [ts, mac] = token.split('.');
    if (!ts || !mac || !/^\d+$/.test(ts)) return false;
    const expected = Buffer.from(sign(ts, key));
    const given = Buffer.from(mac.padEnd(expected.length).slice(0, expected.length));
    if (given.length !== expected.length || !timingSafeEqual(given, expected)) return false;
    const age = Date.now() - Number(ts);
    const min = config.NODE_ENV === 'test' ? 0 : MIN_AGE_MS;
    return age >= min && age <= MAX_AGE_MS;
  }

  async function capture(key: string): Promise<CaptureConfig & { companyId: number }> {
    if (!/^[A-Za-z0-9_-]{8,64}$/.test(key)) throw new HttpError(404, 'NOT_FOUND', 'Not found');
    const rows = await db.setting.findMany({ where: { key: 'leadCapture' } });
    const row = rows.find((r) => (r.value as CaptureConfig | null)?.key === key);
    const v = row?.value as CaptureConfig | undefined;
    if (!row || !v?.enabled) throw new HttpError(404, 'NOT_FOUND', 'Not found');
    return { ...v, companyId: row.companyId };
  }

  const cors = (req: Request, res: Response, allowed: string[]) => {
    const origin = req.headers.origin;
    res.setHeader('Vary', 'Origin');
    if (origin && allowed.includes(origin)) {
      res.setHeader('Access-Control-Allow-Origin', origin);
      res.setHeader('Access-Control-Allow-Methods', 'GET, POST, OPTIONS');
      res.setHeader('Access-Control-Allow-Headers', 'Content-Type');
      res.setHeader('Access-Control-Max-Age', '600');
      return true;
    }
    return !origin; // same-origin/server calls carry no Origin header
  };

  router.use(
    '/api/v1/public/leads',
    rateLimit({
      windowMs: 60_000,
      limit: config.NODE_ENV === 'test' ? 100_000 : 10,
      standardHeaders: 'draft-7',
      legacyHeaders: false,
      message: {
        error: { code: 'RATE_LIMITED', message: 'Too many requests, please try again later' },
      },
    }),
  );

  router.options(
    '/api/v1/public/leads/:key',
    asyncHandler(async (req, res) => {
      const c = await capture(String(req.params.key));
      cors(req, res, c.allowedOrigins ?? []);
      res.status(204).end();
    }),
  );
  router.options(
    '/api/v1/public/leads/:key/challenge',
    asyncHandler(async (req, res) => {
      const c = await capture(String(req.params.key));
      cors(req, res, c.allowedOrigins ?? []);
      res.status(204).end();
    }),
  );

  router.get(
    '/api/v1/public/leads/:key/challenge',
    asyncHandler(async (req, res) => {
      const key = String(req.params.key);
      const c = await capture(key);
      if (!cors(req, res, c.allowedOrigins ?? []))
        throw new HttpError(403, 'FORBIDDEN', 'This site is not allowed to use this form');
      res.setHeader('Cache-Control', 'no-store');
      res.json({ token: makeToken(key), turnstileSiteKey: config.TURNSTILE_SITE_KEY ?? null });
    }),
  );

  router.post(
    '/api/v1/public/leads/:key',
    express.json({ limit: '20kb' }),
    asyncHandler(async (req, res) => {
      const key = String(req.params.key);
      const c = await capture(key);
      if (!cors(req, res, c.allowedOrigins ?? []))
        throw new HttpError(403, 'FORBIDDEN', 'This site is not allowed to use this form');
      const parsed = bodySchema.safeParse(req.body);
      if (!parsed.success)
        throw new HttpError(
          400,
          'VALIDATION_ERROR',
          'Please check your name, email and phone number',
        );
      const b = parsed.data;
      // A bot filled the hidden field: pretend it worked so it learns nothing.
      if (b.website_url) {
        res.status(201).json({ ok: true });
        return;
      }
      if (!checkToken(b.token, key))
        throw new HttpError(400, 'FORM_EXPIRED', 'Please reload the page and try again');
      if (!b.email && !b.phone)
        throw new HttpError(
          400,
          'VALIDATION_ERROR',
          'Please give an email or a phone number so we can reply',
        );
      if (config.TURNSTILE_SECRET) {
        const ok = await verifyTurnstile(
          config.TURNSTILE_SECRET,
          b['cf-turnstile-response'],
          req.ip,
        );
        if (!ok)
          throw new HttpError(
            400,
            'CAPTCHA_FAILED',
            'Please complete the verification and try again',
          );
      }

      const ownerId =
        c.ownerId ??
        (
          await db.user.findFirst({
            where: {
              companyId: c.companyId,
              status: 'active',
              roles: { some: { role: { name: 'Owner' } } },
            },
            select: { id: true },
          })
        )?.id;
      if (!ownerId) throw new HttpError(503, 'UNAVAILABLE', 'Not available right now');
      const auth = await loadAuthContext(db, ownerId, c.companyId);
      if (!auth) throw new HttpError(503, 'UNAVAILABLE', 'Not available right now');

      const service = (SERVICE_INTERESTS as readonly string[]).includes(b.service ?? '')
        ? (b.service as (typeof SERVICE_INTERESTS)[number])
        : null;
      const phone = b.phone || null;
      const dupes = await findLeadDuplicates(db, c.companyId, {
        name: undefined,
        email: b.email || null,
        phone,
      });
      const existing = dupes.leads.find((l) => l.status === 'open');
      if (existing) {
        // Same person writing again: add to the existing lead instead of creating another.
        await logActivity(
          db,
          existing.id,
          'website',
          `Submitted the website form again${b.message ? `: ${b.message}` : ''}`,
          null,
        );
        await db.notification.create({
          data: {
            companyId: c.companyId,
            userId: ownerId,
            title: `${existing.name} contacted you again through the website`,
            link: `/crm/leads/${existing.id}`,
          },
        });
        res.status(201).json({ ok: true });
        return;
      }
      const lead = await db.$transaction((tx) =>
        createLead(
          tx,
          auth,
          {
            name: b.name,
            email: b.email || null,
            phone,
            source: LEAD_SOURCES[0],
            serviceInterest: service,
            estimatedValue: '0',
            currency: 'UGX',
            pipelineId: c.pipelineId ?? undefined,
            ownerId,
            notes: b.message ?? null,
            tags: ['website'],
          },
          { website: { detail: b.page } },
        ),
      );
      await scheduleLeadFollowUp(db, lead);
      await db.notification.create({
        data: {
          companyId: c.companyId,
          userId: ownerId,
          title: `New website lead: ${lead.name}`,
          link: `/crm/leads/${lead.id}`,
        },
      });
      const owner = await db.user.findUnique({ where: { id: ownerId }, select: { email: true } });
      if (owner?.email) {
        await queueEmail(db, {
          companyId: c.companyId,
          to: [owner.email],
          subject: `New website lead: ${lead.name}`.replace(/[\r\n]+/g, ' '),
          text: `A new lead came in through the website form.\n\nName: ${lead.name}\nEmail: ${lead.email ?? '-'}\nPhone: ${lead.phone ?? '-'}\nInterest: ${lead.serviceInterest ?? '-'}\n\n${b.message ?? ''}\n\nOpen it: ${config.APP_URL.replace(/\/$/, '')}/crm/leads/${lead.id}`,
          docType: 'lead',
          docId: lead.id,
          templateName: 'lead_notification',
        }).catch(() => undefined);
      }
      res.status(201).json({ ok: true });
    }),
  );

  return router;
}

async function verifyTurnstile(
  secret: string,
  token: string | undefined,
  ip: string | undefined,
): Promise<boolean> {
  if (!token) return false;
  try {
    const r = await fetch('https://challenges.cloudflare.com/turnstile/v0/siteverify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ secret, response: token, ...(ip ? { remoteip: ip } : {}) }),
    });
    return !!((await r.json()) as { success?: boolean }).success;
  } catch {
    return false;
  }
}
